<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-account-tie me-2"></i>{{ $t('page.staff', 'Staff & HR') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <button v-if="tab==='staff'" class="hms-btn hms-btn-primary" @click="openAddStaff"><i class="mdi mdi-account-plus me-1"></i>Add Staff</button>
          <button v-if="tab==='licenses'" class="hms-btn hms-btn-primary" @click="openAddLicense"><i class="mdi mdi-card-account-details-outline me-1"></i>Add Licence</button>
          <button v-if="tab==='trainings'" class="hms-btn hms-btn-primary" @click="openAddTraining"><i class="mdi mdi-school me-1"></i>Record Training</button>
          <button v-if="tab==='shifts'" class="hms-btn hms-btn-primary" @click="openAddShift"><i class="mdi mdi-clock-time-four me-1"></i>Schedule Shift</button>
          <button v-if="tab==='leave'" class="hms-btn hms-btn-primary" @click="openAddLeave"><i class="mdi mdi-airplane me-1"></i>Apply Leave</button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="tab='staff'" :class="{ selected: tab==='staff' }">
          <div class="hms-stat-num">{{ staffList.length }}</div>
          <div class="hms-stat-lbl">Total Staff</div>
        </div>
        <div class="hms-stat" @click="tab='licenses'" :class="{ selected: tab==='licenses' }">
          <div class="hms-stat-num hms-stat-amber">{{ expiringLicensesCount }}</div>
          <div class="hms-stat-lbl">Licences Expiring</div>
        </div>
        <div class="hms-stat" @click="tab='trainings'" :class="{ selected: tab==='trainings' }">
          <div class="hms-stat-num hms-stat-info">{{ totalCmePoints }}</div>
          <div class="hms-stat-lbl">CME Points (YTD)</div>
        </div>
        <div class="hms-stat" @click="tab='shifts'" :class="{ selected: tab==='shifts' }">
          <div class="hms-stat-num hms-stat-green">{{ todayShiftsCount }}</div>
          <div class="hms-stat-lbl">Today's Shifts</div>
        </div>
        <div class="hms-stat" @click="tab='leave'" :class="{ selected: tab==='leave' }">
          <div class="hms-stat-num hms-stat-purple">{{ pendingLeaveCount }}</div>
          <div class="hms-stat-lbl">Pending Leave</div>
        </div>
        <div class="hms-stat" @click="tab='appraisals'" :class="{ selected: tab==='appraisals' }">
          <div class="hms-stat-num">{{ appraisals.length }}</div>
          <div class="hms-stat-lbl">Appraisals</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab==='staff' }" @click="tab='staff'"><i class="mdi mdi-account-multiple me-1"></i>Staff</button>
        <button class="hms-tab" :class="{ active: tab==='licenses' }" @click="tab='licenses'"><i class="mdi mdi-card-account-details me-1"></i>Licences <span v-if="expiringLicensesCount" class="hms-tab-count">{{ expiringLicensesCount }}</span></button>
        <button class="hms-tab" :class="{ active: tab==='trainings' }" @click="tab='trainings'"><i class="mdi mdi-school me-1"></i>Trainings / CME</button>
        <button class="hms-tab" :class="{ active: tab==='shifts' }" @click="tab='shifts'"><i class="mdi mdi-clock-outline me-1"></i>Shifts</button>
        <button class="hms-tab" :class="{ active: tab==='leave' }" @click="tab='leave'"><i class="mdi mdi-airplane me-1"></i>Leave <span v-if="pendingLeaveCount" class="hms-tab-count">{{ pendingLeaveCount }}</span></button>
        <button class="hms-tab" :class="{ active: tab==='appraisals' }" @click="tab='appraisals'"><i class="mdi mdi-clipboard-check me-1"></i>Appraisals</button>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <!-- ===== STAFF ===== -->
      <div v-else-if="tab==='staff'" class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap"><i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="search" placeholder="Search name, staff#, role, department..." />
          </div>
          <select class="hms-input hms-filter" v-model="roleFilter">
            <option value="">All roles</option>
            <option v-for="r in roles" :key="r.role_id" :value="r.role_id">{{ r.role_name }}</option>
          </select>
          <span class="hms-muted ms-auto">{{ filteredStaff.length }} staff</span>
        </div>

        <div v-if="filteredStaff.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr><th>Staff</th><th>Role</th><th>Department</th><th>Specialization</th><th>Phone</th><th>Licence</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="s in filteredStaff" :key="s.staff_id">
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <div class="hms-avatar">{{ initials(s) }}</div>
                    <div>
                      <div class="hms-text fw-semibold">{{ s.first_name }} {{ s.last_name }}</div>
                      <div class="hms-muted-sm">{{ s.staff_number }}</div>
                    </div>
                  </div>
                </td>
                <td>{{ roleName(s.role_id) }}</td>
                <td>{{ s.department || '—' }}</td>
                <td>{{ s.specialization || '—' }}</td>
                <td>{{ s.phone || '—' }}</td>
                <td>
                  <div v-if="s.license_number" class="hms-mono">{{ s.license_number }}</div>
                  <div v-if="s.license_expiry" class="hms-muted-sm" :class="licenseClass(s.license_expiry)">exp {{ formatDate(s.license_expiry) }}</div>
                  <span v-if="!s.license_number" class="hms-muted-sm">—</span>
                </td>
                <td><span class="hms-badge" :class="staffStatusBadge(s.status)">{{ s.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-account-multiple-outline"></i>No staff yet.</div>
      </div>

      <!-- ===== LICENCES ===== -->
      <div v-else-if="tab==='licenses'" class="hms-card">
        <div class="d-flex justify-content-between mb-3">
          <span class="hms-muted">{{ licenses.length }} licences across {{ uniqueLicenseStaff }} staff</span>
        </div>
        <div v-if="licenses.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr><th>Staff</th><th>Regulator</th><th>License No</th><th>Category</th><th>Issued</th><th>Expires</th><th>Days Left</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="l in licenses" :key="l.license_id" :class="rowExpiryClass(l.days_to_expiry)">
                <td>
                  <div class="hms-text fw-semibold">{{ l.staff_name }}</div>
                  <div class="hms-muted-sm">{{ l.staff_number }}</div>
                </td>
                <td><span class="hms-badge hms-badge-info">{{ l.regulator }}</span></td>
                <td class="hms-mono">{{ l.license_number }}</td>
                <td>{{ l.license_category || '—' }}</td>
                <td>{{ formatDate(l.issue_date) }}</td>
                <td>{{ formatDate(l.expiry_date) }}</td>
                <td><span :class="daysLeftClass(l.days_to_expiry)">{{ l.days_to_expiry !== null ? l.days_to_expiry + 'd' : '—' }}</span></td>
                <td><span class="hms-badge" :class="renewalBadge(l.renewal_status, l.days_to_expiry)">{{ formatRenewal(l.renewal_status, l.days_to_expiry) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-card-account-details-outline"></i>No licences recorded.</div>
      </div>

      <!-- ===== TRAININGS ===== -->
      <div v-else-if="tab==='trainings'" class="hms-card">
        <span class="hms-muted">{{ trainings.length }} trainings · {{ totalCmePoints }} CME points logged</span>
        <div v-if="trainings.length" class="mt-3" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr><th>Staff</th><th>Title</th><th>Type</th><th>Provider</th><th>Dates</th><th>Hours</th><th>CME Pts</th><th>Outcome</th></tr></thead>
            <tbody>
              <tr v-for="t in trainings" :key="t.training_id">
                <td><div class="hms-text fw-semibold">{{ t.staff_name }}</div><div class="hms-muted-sm">{{ t.staff_number }}</div></td>
                <td>{{ t.training_title }}</td>
                <td><span class="hms-badge hms-badge-info">{{ formatTrainingType(t.training_type) }}</span></td>
                <td>{{ t.provider || '—' }}</td>
                <td>{{ formatDate(t.start_date) }} — {{ formatDate(t.end_date) }}</td>
                <td>{{ t.duration_hours || '—' }}</td>
                <td><strong>{{ t.cme_points || 0 }}</strong></td>
                <td><span class="hms-badge" :class="outcomeBadge(t.outcome)">{{ t.outcome }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty mt-3"><i class="mdi mdi-school-outline"></i>No trainings recorded.</div>
      </div>

      <!-- ===== SHIFTS ===== -->
      <div v-else-if="tab==='shifts'" class="hms-card">
        <span class="hms-muted">{{ shifts.length }} scheduled · {{ todayShiftsCount }} today</span>
        <div v-if="shifts.length" class="mt-3" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr><th>Staff</th><th>Date</th><th>Shift</th><th>Time</th><th>Ward / Dept</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="sh in shifts" :key="sh.shift_id" :class="(sh.shift_date || '').slice(0,10) === todayIso ? 'hms-row-today' : ''">
                <td>{{ sh.staff_name || ('Staff #' + sh.staff_id) }}</td>
                <td>{{ formatDate(sh.shift_date) }}</td>
                <td><span class="hms-badge" :class="shiftBadge(sh.shift_type)">{{ formatShift(sh.shift_type) }}</span></td>
                <td>{{ sh.start_time || '—' }} – {{ sh.end_time || '—' }}</td>
                <td>{{ sh.ward_name || sh.dept_name || '—' }}</td>
                <td><span class="hms-badge" :class="shiftStatusBadge(sh.status)">{{ sh.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty mt-3"><i class="mdi mdi-clock-outline"></i>No shifts scheduled.</div>
      </div>

      <!-- ===== LEAVE ===== -->
      <div v-else-if="tab==='leave'" class="hms-card">
        <span class="hms-muted">{{ leaveRequests.length }} requests · {{ pendingLeaveCount }} pending</span>
        <div v-if="leaveRequests.length" class="mt-3" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr><th>Staff</th><th>Type</th><th>Period</th><th>Days</th><th>Reason</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              <tr v-for="lr in leaveRequests" :key="lr.leave_id">
                <td>{{ lr.staff_name || ('Staff #' + lr.staff_id) }}</td>
                <td><span class="hms-badge hms-badge-info">{{ lr.leave_type }}</span></td>
                <td>{{ formatDate(lr.start_date) }} – {{ formatDate(lr.end_date) }}</td>
                <td><strong>{{ lr.days_requested }}</strong></td>
                <td><span class="hms-text-clip">{{ lr.reason || '—' }}</span></td>
                <td><span class="hms-badge" :class="leaveBadge(lr.status)">{{ lr.status }}</span></td>
                <td>
                  <button v-if="lr.status === 'pending'" class="hms-btn hms-btn-sm hms-btn-success me-1" @click="approveLeave(lr)">Approve</button>
                  <button v-if="lr.status === 'pending'" class="hms-btn hms-btn-sm hms-btn-red" @click="openRejectLeave(lr)">Reject</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty mt-3"><i class="mdi mdi-airplane"></i>No leave requests.</div>
      </div>

      <!-- ===== APPRAISALS ===== -->
      <div v-else-if="tab==='appraisals'" class="hms-card">
        <span class="hms-muted">{{ appraisals.length }} appraisals</span>
        <div v-if="appraisals.length" class="mt-3" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr><th>Staff</th><th>Period</th><th>Type</th><th>Appraiser</th><th>Score</th><th>Rating</th><th>Recommendation</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="a in appraisals" :key="a.appraisal_id">
                <td>{{ a.staff_name }}</td>
                <td>{{ a.period_label || a.period_year }}</td>
                <td><span class="hms-badge hms-badge-info">{{ a.appraisal_type }}</span></td>
                <td>{{ a.appraiser_name || '—' }}</td>
                <td><strong>{{ a.overall_score != null ? a.overall_score : '—' }}</strong></td>
                <td><span class="hms-badge" :class="ratingBadge(a.overall_rating)">{{ a.overall_rating || '—' }}</span></td>
                <td>{{ a.recommendation || '—' }}</td>
                <td><span class="hms-badge" :class="appraisalStatusBadge(a.status)">{{ a.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty mt-3"><i class="mdi mdi-clipboard-check-outline"></i>No appraisals recorded.</div>
      </div>

      <!-- ============ MODALS ============ -->

      <!-- Add Staff -->
      <div class="hms-modal-overlay" v-if="showAddStaff" @click.self="showAddStaff=false">
        <div class="hms-modal">
          <div class="hms-modal-title"><span><i class="mdi mdi-account-plus me-2"></i>Add Staff</span><button class="hms-modal-close" @click="showAddStaff=false">&times;</button></div>
          <div v-if="staffError" class="hms-alert hms-alert-danger">{{ staffError }}</div>
          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">First Name *</label><input class="hms-input" v-model="staffForm.first_name" /></div>
            <div class="col-md-6"><label class="hms-label">Last Name *</label><input class="hms-input" v-model="staffForm.last_name" /></div>
            <div class="col-md-4"><label class="hms-label">Gender</label>
              <select class="hms-input" v-model="staffForm.gender">
                <option value="">Select...</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">DOB</label><input type="date" class="hms-input" v-model="staffForm.dob" /></div>
            <div class="col-md-4"><label class="hms-label">National ID</label><input class="hms-input" v-model="staffForm.national_id" /></div>
            <div class="col-md-6"><label class="hms-label">Phone</label><input class="hms-input" v-model="staffForm.phone" /></div>
            <div class="col-md-6"><label class="hms-label">Email</label><input type="email" class="hms-input" v-model="staffForm.email" /></div>
            <div class="col-md-6"><label class="hms-label">Role</label>
              <select class="hms-input" v-model="staffForm.role_id">
                <option value="">Select...</option>
                <option v-for="r in roles" :key="r.role_id" :value="r.role_id">{{ r.role_name }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Department</label><input class="hms-input" v-model="staffForm.department" /></div>
            <div class="col-md-6"><label class="hms-label">Specialization</label><input class="hms-input" v-model="staffForm.specialization" /></div>
            <div class="col-md-6"><label class="hms-label">Employment Type</label>
              <select class="hms-input" v-model="staffForm.employment_type">
                <option value="permanent">Permanent</option>
                <option value="contract">Contract</option>
                <option value="locum">Locum</option>
                <option value="intern">Intern</option>
                <option value="student">Student (on placement)</option>
                <option value="volunteer">Volunteer</option>
                <option value="consultant">Consultant (visiting)</option>
              </select>
            </div>
            <div class="col-md-6" v-if="staffForm.employment_type === 'student'">
              <label class="hms-label">Student Admission Number *</label>
              <input class="hms-input" v-model="staffForm.student_admission_no" placeholder="e.g. KMTC/12345/2024" />
            </div>
            <div class="col-md-6"><label class="hms-label">License Number</label><input class="hms-input" v-model="staffForm.license_number" /></div>
            <div class="col-md-6"><label class="hms-label">License Expiry</label><input type="date" class="hms-input" v-model="staffForm.license_expiry" /></div>
            <div class="col-md-12"><label class="hms-label">Profile / Passport Photo URL</label>
              <input class="hms-input" v-model="staffForm.profile_photo" placeholder="https://… or /uploads/staff/<id>.jpg" />
              <div class="hms-muted-sm mt-1">Paste an image URL — used on the staff card and the on-shift dashboard.</div>
            </div>
          </div>
          <button class="hms-btn hms-btn-primary w-100" @click="doAddStaff" :disabled="processing">{{ processing ? 'Saving...' : 'Add Staff' }}</button>
        </div>
      </div>

      <!-- Add Licence -->
      <div class="hms-modal-overlay" v-if="showAddLicense" @click.self="showAddLicense=false">
        <div class="hms-modal" style="max-width:560px;">
          <div class="hms-modal-title"><span><i class="mdi mdi-card-account-details me-2"></i>Add Licence</span><button class="hms-modal-close" @click="showAddLicense=false">&times;</button></div>
          <div v-if="licError" class="hms-alert hms-alert-danger">{{ licError }}</div>
          <div class="row g-3 mb-3">
            <div class="col-12"><label class="hms-label">Staff *</label>
              <select class="hms-input" v-model="licForm.staff_id">
                <option value="">Select...</option>
                <option v-for="s in staffList" :key="s.staff_id" :value="s.staff_id">{{ s.first_name }} {{ s.last_name }} ({{ s.staff_number }})</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Regulator *</label>
              <select class="hms-input" v-model="licForm.regulator">
                <option v-for="r in regulators" :key="r" :value="r">{{ r }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">License Number *</label><input class="hms-input" v-model="licForm.license_number" /></div>
            <div class="col-12"><label class="hms-label">Category</label><input class="hms-input" v-model="licForm.license_category" placeholder="e.g. Medical Practitioner" /></div>
            <div class="col-md-6"><label class="hms-label">Issue Date</label><input type="date" class="hms-input" v-model="licForm.issue_date" /></div>
            <div class="col-md-6"><label class="hms-label">Expiry Date</label><input type="date" class="hms-input" v-model="licForm.expiry_date" /></div>
          </div>
          <button class="hms-btn hms-btn-primary w-100" @click="doAddLicense" :disabled="processing">{{ processing ? 'Saving...' : 'Add Licence' }}</button>
        </div>
      </div>

      <!-- Add Training -->
      <div class="hms-modal-overlay" v-if="showAddTraining" @click.self="showAddTraining=false">
        <div class="hms-modal" style="max-width:600px;">
          <div class="hms-modal-title"><span><i class="mdi mdi-school me-2"></i>Record Training</span><button class="hms-modal-close" @click="showAddTraining=false">&times;</button></div>
          <div v-if="trainError" class="hms-alert hms-alert-danger">{{ trainError }}</div>
          <div class="row g-3 mb-3">
            <div class="col-12"><label class="hms-label">Staff *</label>
              <select class="hms-input" v-model="trainForm.staff_id">
                <option value="">Select...</option>
                <option v-for="s in staffList" :key="s.staff_id" :value="s.staff_id">{{ s.first_name }} {{ s.last_name }}</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Training Title *</label><input class="hms-input" v-model="trainForm.training_title" /></div>
            <div class="col-md-4"><label class="hms-label">Type</label>
              <select class="hms-input" v-model="trainForm.training_type">
                <option value="cme">CME</option><option value="in_service">In-service</option><option value="short_course">Short Course</option><option value="workshop">Workshop</option><option value="conference">Conference</option><option value="certification">Certification</option><option value="orientation">Orientation</option><option value="mentorship">Mentorship</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">CME Points</label><input type="number" step="0.5" class="hms-input" v-model.number="trainForm.cme_points" /></div>
            <div class="col-md-4"><label class="hms-label">Hours</label><input type="number" class="hms-input" v-model.number="trainForm.duration_hours" /></div>
            <div class="col-md-6"><label class="hms-label">Provider</label><input class="hms-input" v-model="trainForm.provider" /></div>
            <div class="col-md-3"><label class="hms-label">Start</label><input type="date" class="hms-input" v-model="trainForm.start_date" /></div>
            <div class="col-md-3"><label class="hms-label">End</label><input type="date" class="hms-input" v-model="trainForm.end_date" /></div>
          </div>
          <button class="hms-btn hms-btn-primary w-100" @click="doAddTraining" :disabled="processing">{{ processing ? 'Saving...' : 'Record Training' }}</button>
        </div>
      </div>

      <!-- Add Shift -->
      <div class="hms-modal-overlay" v-if="showAddShift" @click.self="showAddShift=false">
        <div class="hms-modal" style="max-width:520px;">
          <div class="hms-modal-title"><span><i class="mdi mdi-clock-time-four me-2"></i>Schedule Shift</span><button class="hms-modal-close" @click="showAddShift=false">&times;</button></div>
          <div v-if="shiftError" class="hms-alert hms-alert-danger">{{ shiftError }}</div>
          <div class="row g-3 mb-3">
            <div class="col-12"><label class="hms-label">Staff *</label>
              <select class="hms-input" v-model="shiftForm.staff_id">
                <option value="">Select...</option>
                <option v-for="s in staffList" :key="s.staff_id" :value="s.staff_id">{{ s.first_name }} {{ s.last_name }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Date *</label><input type="date" class="hms-input" v-model="shiftForm.shift_date" /></div>
            <div class="col-md-6"><label class="hms-label">Shift Type</label>
              <select class="hms-input" v-model="shiftForm.shift_type">
                <option value="morning">Morning</option><option value="afternoon">Afternoon</option><option value="night">Night</option><option value="on_call">On-call</option><option value="custom">Custom</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Start</label><input type="time" class="hms-input" v-model="shiftForm.start_time" /></div>
            <div class="col-md-6"><label class="hms-label">End</label><input type="time" class="hms-input" v-model="shiftForm.end_time" /></div>
          </div>
          <button class="hms-btn hms-btn-primary w-100" @click="doAddShift" :disabled="processing">{{ processing ? 'Saving...' : 'Schedule' }}</button>
        </div>
      </div>

      <!-- Apply Leave -->
      <div class="hms-modal-overlay" v-if="showAddLeave" @click.self="showAddLeave=false">
        <div class="hms-modal" style="max-width:520px;">
          <div class="hms-modal-title"><span><i class="mdi mdi-airplane me-2"></i>Apply for Leave</span><button class="hms-modal-close" @click="showAddLeave=false">&times;</button></div>
          <div v-if="leaveError" class="hms-alert hms-alert-danger">{{ leaveError }}</div>
          <div class="row g-3 mb-3">
            <div class="col-12"><label class="hms-label">Staff *</label>
              <select class="hms-input" v-model="leaveForm.staff_id">
                <option value="">Select...</option>
                <option v-for="s in staffList" :key="s.staff_id" :value="s.staff_id">{{ s.first_name }} {{ s.last_name }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Type</label>
              <select class="hms-input" v-model="leaveForm.leave_type">
                <option value="annual">Annual</option><option value="sick">Sick</option><option value="maternity">Maternity</option><option value="paternity">Paternity</option><option value="study">Study</option><option value="compassionate">Compassionate</option><option value="unpaid">Unpaid</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-md-3"><label class="hms-label">Start</label><input type="date" class="hms-input" v-model="leaveForm.start_date" /></div>
            <div class="col-md-3"><label class="hms-label">End</label><input type="date" class="hms-input" v-model="leaveForm.end_date" /></div>
            <div class="col-12"><label class="hms-label">Reason</label><textarea class="hms-input" rows="2" v-model="leaveForm.reason"></textarea></div>
          </div>
          <button class="hms-btn hms-btn-primary w-100" @click="doAddLeave" :disabled="processing">{{ processing ? 'Saving...' : 'Submit' }}</button>
        </div>
      </div>

      <!-- Reject leave -->
      <div class="hms-modal-overlay" v-if="rejectTarget" @click.self="rejectTarget=null">
        <div class="hms-modal" style="max-width:480px;">
          <div class="hms-modal-title"><span><i class="mdi mdi-close-circle me-2"></i>Reject Leave</span><button class="hms-modal-close" @click="rejectTarget=null">&times;</button></div>
          <p class="hms-muted">{{ rejectTarget.staff_name }} · {{ rejectTarget.leave_type }} · {{ formatDate(rejectTarget.start_date) }}–{{ formatDate(rejectTarget.end_date) }}</p>
          <div class="mb-3"><label class="hms-label">Reason</label><textarea class="hms-input" rows="3" v-model="rejectReason"></textarea></div>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-blue flex-fill" @click="rejectTarget=null">Cancel</button>
            <button class="hms-btn hms-btn-red flex-fill" @click="doRejectLeave" :disabled="processing">Confirm Reject</button>
          </div>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }
.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.hms-text-clip { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 130px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-amber { color: #f1b44c; }
.hms-stat-info { color: var(--hms-primary); }
.hms-stat-purple { color: #9b59b6; }
.hms-stat-green { color: var(--hms-success); }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }
.hms-tab-count { background: var(--hms-primary); color: #fff; border-radius: 999px; padding: 1px 7px; font-size: 10px; margin-left: 8px; }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 240px; max-width: 360px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: middle; }
.hms-table tr:hover td { background: var(--hms-primary-lt); }
.hms-row-today td   { background: rgba(40,167,69,.06); }
.hms-row-soon td    { background: rgba(245,158,11,.06); }
.hms-row-expired td { background: rgba(220,53,69,.08); }

.hms-avatar { width: 36px; height: 36px; border-radius: 50%; background: var(--hms-primary-lt); color: var(--hms-primary); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 12px; flex-shrink: 0; }

.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; text-transform: capitalize; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-amber   { background: rgba(245,158,11,.15); color: #f59e0b; }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-red { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-red:hover:not(:disabled) { background: rgba(220,53,69,.25); }
.hms-btn-success { background: rgba(40,167,69,.15); color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-btn-success:hover:not(:disabled) { background: var(--hms-success); color: #fff; }

.text-amber-strong { color: #f59e0b; font-weight: 700; }
.text-red-strong   { color: var(--hms-danger); font-weight: 700; }
.text-green-strong { color: var(--hms-success); font-weight: 600; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "StaffManagement",
  components: { Navigation },
  data() {
    return {
      loading: true,
      processing: false,
      tab: "staff",
      search: "",
      roleFilter: "",

      staffList: [],
      roles: [],
      licenses: [],
      trainings: [],
      shifts: [],
      leaveRequests: [],
      appraisals: [],

      regulators: ['KMPDC','NCK','PPB','KMLTTB','KCHA','KNDI','COHK','PSK','OTHER'],

      showAddStaff: false, staffError: "", staffForm: this.emptyStaff(),
      showAddLicense: false, licError: "", licForm: this.emptyLicense(),
      showAddTraining: false, trainError: "", trainForm: this.emptyTraining(),
      showAddShift: false, shiftError: "", shiftForm: this.emptyShift(),
      showAddLeave: false, leaveError: "", leaveForm: this.emptyLeave(),

      rejectTarget: null, rejectReason: "",

      toastMsg: "", toastError: false, toastTimer: null,
      branchId: sessionStorage.getItem("branchId")
    };
  },
  computed: {
    todayIso() { return new Date().toISOString().slice(0,10); },
    filteredStaff() {
      var list = this.staffList;
      if (this.roleFilter) list = list.filter(s => s.role_id == this.roleFilter);
      var q = this.search.trim().toLowerCase();
      if (q) list = list.filter(s => ((s.first_name||"")+" "+(s.last_name||"")+" "+(s.staff_number||"")+" "+(s.department||"")+" "+(s.specialization||"")).toLowerCase().indexOf(q) >= 0);
      return list;
    },
    expiringLicensesCount() { return this.licenses.filter(l => l.days_to_expiry !== null && l.days_to_expiry <= 90).length; },
    totalCmePoints() { return this.trainings.reduce((s,t) => s + (parseFloat(t.cme_points) || 0), 0); },
    todayShiftsCount() { return this.shifts.filter(sh => (sh.shift_date || "").slice(0,10) === this.todayIso).length; },
    pendingLeaveCount() { return this.leaveRequests.filter(l => l.status === 'pending').length; },
    uniqueLicenseStaff() { var s = new Set(); this.licenses.forEach(l => s.add(l.staff_id)); return s.size; }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.loadAll();
  },
  beforeUnmount() { if (this.toastTimer) clearTimeout(this.toastTimer); },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    emptyStaff() { return { first_name:"", last_name:"", gender:"", dob:"", national_id:"", phone:"", email:"", role_id:"", department:"", specialization:"", employment_type:"permanent", license_number:"", license_expiry:"", student_admission_no:"", profile_photo:"" }; },
    emptyLicense() { return { staff_id:"", regulator:"OTHER", license_number:"", license_category:"", issue_date:"", expiry_date:"" }; },
    emptyTraining() { return { staff_id:"", training_title:"", training_type:"cme", provider:"", cme_points:0, duration_hours:0, start_date:"", end_date:"" }; },
    emptyShift() { return { staff_id:"", shift_date: this.todayIso, shift_type:"morning", start_time:"08:00", end_time:"17:00" }; },
    emptyLeave() { return { staff_id:"", leave_type:"annual", start_date:"", end_date:"", reason:"" }; },

    initials(s) { var f = (s.first_name||"")[0]||""; var l = (s.last_name||"")[0]||""; return ((f+l).toUpperCase()) || "?"; },
    roleName(rid) { var r = this.roles.find(x => x.role_id == rid); return r ? r.role_name : '—'; },

    formatDate(d) { if (!d) return "—"; try { var dt=new Date(d); if(isNaN(dt.getTime())) return d; return dt.toLocaleDateString(undefined,{day:'2-digit',month:'short',year:'numeric'}); } catch(e){return d;} },
    formatTrainingType(t) { return ({ cme:'CME', in_service:'In-service', short_course:'Short Course', workshop:'Workshop', conference:'Conference', orientation:'Orientation', certification:'Certification', mentorship:'Mentorship', other:'Other' })[t] || t; },
    formatShift(s) { return ({ morning:'Morning', afternoon:'Afternoon', night:'Night', on_call:'On-call', custom:'Custom' })[s] || s; },
    formatRenewal(s, days) {
      if (days !== null && days < 0) return 'Expired';
      if (days !== null && days <= 90) return 'Due Soon';
      return ({ valid:'Valid', due_soon:'Due Soon', expired:'Expired', renewing:'Renewing', suspended:'Suspended', revoked:'Revoked' })[s] || s;
    },

    licenseClass(d) {
      if (!d) return "";
      var dt = new Date(d); var now = new Date();
      var days = Math.floor((dt - now) / 86400000);
      if (days < 0) return "text-red-strong";
      if (days <= 90) return "text-amber-strong";
      return "text-green-strong";
    },
    rowExpiryClass(days) {
      if (days === null || days === undefined) return "";
      if (days < 0) return "hms-row-expired";
      if (days <= 90) return "hms-row-soon";
      return "";
    },
    daysLeftClass(days) {
      if (days === null || days === undefined) return "hms-muted-sm";
      if (days < 0) return "text-red-strong";
      if (days <= 90) return "text-amber-strong";
      return "text-green-strong";
    },
    renewalBadge(s, days) {
      if (days !== null && days < 0) return 'hms-badge-danger';
      if (days !== null && days <= 90) return 'hms-badge-warning';
      var m = { valid:'hms-badge-success', due_soon:'hms-badge-warning', expired:'hms-badge-danger', renewing:'hms-badge-info', suspended:'hms-badge-danger', revoked:'hms-badge-danger' };
      return m[s] || 'hms-badge-neutral';
    },
    staffStatusBadge(s) { return ({ active:'hms-badge-success', inactive:'hms-badge-neutral', suspended:'hms-badge-warning', terminated:'hms-badge-danger' })[s] || 'hms-badge-neutral'; },
    shiftBadge(s) { return ({ morning:'hms-badge-info', afternoon:'hms-badge-amber', night:'hms-badge-purple', on_call:'hms-badge-warning' })[s] || 'hms-badge-neutral'; },
    shiftStatusBadge(s) { return ({ scheduled:'hms-badge-info', active:'hms-badge-success', completed:'hms-badge-neutral', absent:'hms-badge-danger', swapped:'hms-badge-warning' })[s] || 'hms-badge-neutral'; },
    leaveBadge(s) { return ({ pending:'hms-badge-amber', approved:'hms-badge-success', rejected:'hms-badge-danger', cancelled:'hms-badge-neutral' })[s] || 'hms-badge-neutral'; },
    outcomeBadge(o) { return ({ completed:'hms-badge-success', in_progress:'hms-badge-amber', failed:'hms-badge-danger', withdrew:'hms-badge-neutral', planned:'hms-badge-info' })[o] || 'hms-badge-neutral'; },
    appraisalStatusBadge(s) { return ({ draft:'hms-badge-neutral', submitted:'hms-badge-info', under_review:'hms-badge-amber', approved:'hms-badge-success', disputed:'hms-badge-danger', closed:'hms-badge-neutral' })[s] || 'hms-badge-neutral'; },
    ratingBadge(r) {
      switch(r) {
        case 'outstanding':     return 'hms-badge-success';
        case 'exceeds':         return 'hms-badge-success';
        case 'meets':           return 'hms-badge-info';
        case 'partially_meets': return 'hms-badge-warning';
        case 'below':           return 'hms-badge-danger';
        default:                return 'hms-badge-neutral';
      }
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    async loadAll() {
      this.loading = true;
      try { await Promise.all([
        this.loadStaff(),
        this.loadRoles(),
        this.loadLicenses(),
        this.loadTrainings(),
        this.loadShifts(),
        this.loadLeave(),
        this.loadAppraisals()
      ]); } catch(e) { console.error(e); }
      this.loading = false;
    },
    async loadStaff()       { try { var r = await executeJson({ function:"getStaff",         branch_id: this.branchId }, hms); if (r.data && r.data.success) this.staffList     = (r.data.data && r.data.data.Staff)       || []; } catch(e){} },
    async loadRoles()       { try { var r = await executeJson({ function:"getRoles" }, hms);                                   if (r.data && r.data.success) this.roles         = (r.data.data && r.data.data.Roles)       || []; } catch(e){} },
    async loadLicenses()    { try { var r = await executeJson({ function:"getStaffLicenses",branch_id: this.branchId }, hms); if (r.data && r.data.success) this.licenses      = (r.data.data && r.data.data.Licenses)    || []; } catch(e){} },
    async loadTrainings()   { try { var r = await executeJson({ function:"getStaffTrainings",branch_id:this.branchId }, hms); if (r.data && r.data.success) this.trainings     = (r.data.data && r.data.data.Trainings)   || []; } catch(e){} },
    async loadShifts()      { try { var r = await executeJson({ function:"getTodayShifts",  branch_id: this.branchId }, hms); if (r.data && r.data.success) this.shifts        = (r.data.data && r.data.data.Shifts)      || []; } catch(e){} },
    async loadLeave()       { try { var r = await executeJson({ function:"getLeaveRequests",branch_id: this.branchId }, hms); if (r.data && r.data.success) this.leaveRequests = (r.data.data && r.data.data.LeaveRequests)|| []; } catch(e){} },
    async loadAppraisals()  { try { var r = await executeJson({ function:"getStaffAppraisals",branch_id:this.branchId }, hms); if (r.data && r.data.success) this.appraisals    = (r.data.data && r.data.data.Appraisals)  || []; } catch(e){} },

    // ===== ADD STAFF =====
    openAddStaff()  { this.staffForm = this.emptyStaff(); this.staffError = ""; this.showAddStaff = true; },
    async doAddStaff() {
      this.staffError = "";
      if (!this.staffForm.first_name || !this.staffForm.last_name) { this.staffError = "First and last name required."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function:"createStaff" }, this.staffForm), hms);
        if (r.data && r.data.success) { this.showToast("Staff added"); this.showAddStaff = false; await this.loadStaff(); }
        else { this.staffError = r.data.message || "Failed."; }
      } catch(e) { this.staffError = "Connection error."; }
      this.processing = false;
    },

    // ===== ADD LICENSE =====
    openAddLicense() { this.licForm = this.emptyLicense(); this.licError = ""; this.showAddLicense = true; },
    async doAddLicense() {
      this.licError = "";
      if (!this.licForm.staff_id || !this.licForm.license_number) { this.licError = "Staff and license number required."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function:"addStaffLicense" }, this.licForm), hms);
        if (r.data && r.data.success) { this.showToast("Licence added"); this.showAddLicense = false; await this.loadLicenses(); }
        else { this.licError = r.data.message || "Failed."; }
      } catch(e) { this.licError = "Connection error."; }
      this.processing = false;
    },

    // ===== ADD TRAINING =====
    openAddTraining() { this.trainForm = this.emptyTraining(); this.trainError = ""; this.showAddTraining = true; },
    async doAddTraining() {
      this.trainError = "";
      if (!this.trainForm.staff_id || !this.trainForm.training_title) { this.trainError = "Staff and title required."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function:"addStaffTraining" }, this.trainForm), hms);
        if (r.data && r.data.success) { this.showToast("Training recorded"); this.showAddTraining = false; await this.loadTrainings(); }
        else { this.trainError = r.data.message || "Failed."; }
      } catch(e) { this.trainError = "Connection error."; }
      this.processing = false;
    },

    // ===== ADD SHIFT =====
    openAddShift()  { this.shiftForm = this.emptyShift(); this.shiftError = ""; this.showAddShift = true; },
    async doAddShift() {
      this.shiftError = "";
      if (!this.shiftForm.staff_id) { this.shiftError = "Pick a staff member."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function:"createShift" }, this.shiftForm), hms);
        if (r.data && r.data.success) { this.showToast("Shift scheduled"); this.showAddShift = false; await this.loadShifts(); }
        else { this.shiftError = r.data.message || "Failed."; }
      } catch(e) { this.shiftError = "Connection error."; }
      this.processing = false;
    },

    // ===== APPLY LEAVE =====
    openAddLeave()  { this.leaveForm = this.emptyLeave(); this.leaveError = ""; this.showAddLeave = true; },
    async doAddLeave() {
      this.leaveError = "";
      if (!this.leaveForm.staff_id || !this.leaveForm.start_date || !this.leaveForm.end_date) { this.leaveError = "Staff and dates required."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function:"applyLeave" }, this.leaveForm), hms);
        if (r.data && r.data.success) { this.showToast("Leave submitted"); this.showAddLeave = false; await this.loadLeave(); }
        else { this.leaveError = r.data.message || "Failed."; }
      } catch(e) { this.leaveError = "Connection error."; }
      this.processing = false;
    },

    async approveLeave(lr) {
      this.processing = true;
      try {
        var r = await executeJson({ function:"approveLeave", leave_id: lr.leave_id }, hms);
        if (r.data && r.data.success) { this.showToast("Leave approved"); await this.loadLeave(); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    openRejectLeave(lr) { this.rejectTarget = lr; this.rejectReason = ""; },
    async doRejectLeave() {
      this.processing = true;
      try {
        var r = await executeJson({ function:"rejectLeave", leave_id: this.rejectTarget.leave_id, rejection_reason: this.rejectReason }, hms);
        if (r.data && r.data.success) { this.showToast("Leave rejected"); this.rejectTarget = null; await this.loadLeave(); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    }
  }
};
</script>
